import { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadBlob, ensurePdfExtension } from "@/shared/utils";
import { proxyS3Url } from "@/shared/utils/proxyS3Url";
import {
  useLazyGetContractDetailQuery,
  useDownloadWorkContractPreviewMutation,
  usePreviewJobApplicationMutation,
  usePreviewOrderOnHiringMutation,
} from "@/features/hr/contracts/api";
import type { CombinedContractListItem } from "@/features/hr/contracts/types";
import { extractErrorMessage } from "@/features/hr/contracts/utils";
import type { PromptState } from "./useContractModals";

export interface UseContractDownloadsReturn {
  fetchContractDetail: ReturnType<typeof useLazyGetContractDetailQuery>[0];
  downloadingContractId: number | null;
  previewingContractId: number | null;
  previewingJobApplicationCandidateId: number | null;
  previewingOrderCandidateId: number | null;
  isDownloading: boolean;
  isPreviewing: boolean;
  handleDownload: (id: number, contract?: CombinedContractListItem) => Promise<void>;
  handlePreview: (id: number) => Promise<void>;
  handlePreviewJobApplication: (candidateId: number) => Promise<void>;
  handlePreviewOrder: (orderId: number) => Promise<void>;
  handleDownloadOriginal: (id: number, contract?: CombinedContractListItem) => void;
  setPrompt: (state: PromptState | null) => void;
}

export function useContractDownloads(setPrompt: (state: PromptState | null) => void): UseContractDownloadsReturn {
  const { t } = useTranslation("ContractsPage");

  const [fetchContractDetail] = useLazyGetContractDetailQuery();
  const [downloadWorkContractPreview] = useDownloadWorkContractPreviewMutation();
  const [previewJobApplication] = usePreviewJobApplicationMutation();
  const [previewOrderOnHiring] = usePreviewOrderOnHiringMutation();

  const [downloadingContractId, setDownloadingContractId] = useState<number | null>(null);
  const [previewingContractId, setPreviewingContractId] = useState<number | null>(null);
  const [previewingJobApplicationCandidateId, setPreviewingJobApplicationCandidateId] = useState<number | null>(null);
  const [previewingOrderCandidateId, setPreviewingOrderCandidateId] = useState<number | null>(null);

  const isDownloading = downloadingContractId !== null;
  const isPreviewing = previewingContractId !== null;

  async function fetchAndDownloadPdf(pdfUrl: string, id: number) {
    const response = await fetch(proxyS3Url(pdfUrl));
    if (!response.ok) throw new Error("Failed to fetch PDF");
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition");
    const filename = disposition?.match(/filename="(.+)"/)?.[1] || t("downloads.contractFileName", { id });
    downloadBlob(blob, ensurePdfExtension(filename));
  }

  async function handleDownload(id: number, contract?: CombinedContractListItem) {
    if (isDownloading) return;

    try {
      setDownloadingContractId(id);

      if (contract?.is_legacy_import && contract?.legacy_contract_pdf_url) {
        await fetchAndDownloadPdf(contract.legacy_contract_pdf_url, id);
        return;
      }

      if (contract?.is_legacy_import) {
        setPrompt({
          title: t("messages.errorTitle"),
          text: t("messages.legacyScanNotAttached"),
          variant: "error",
        });
        return;
      }

      if (contract?.signing_document?.signed_pdf_url) {
        await fetchAndDownloadPdf(contract.signing_document.signed_pdf_url, id);
        return;
      }

      const data = await fetchContractDetail(id).unwrap();
      const pdfUrl = data.signing_document?.signed_pdf_url;

      if (pdfUrl) {
        await fetchAndDownloadPdf(pdfUrl, id);
      } else {
        const { blob, filename } = await downloadWorkContractPreview(id).unwrap();
        downloadBlob(blob, ensurePdfExtension(filename || t("downloads.contractFileName", { id })));
      }
    } catch (error: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.downloadErrorText", t),
        variant: "error",
      });
    } finally {
      setDownloadingContractId(null);
    }
  }

  async function handlePreview(id: number) {
    if (isPreviewing) return;

    try {
      setPreviewingContractId(id);
      const { blob, filename } = await downloadWorkContractPreview(id).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || t("downloads.contractFileName", { id })));
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.previewErrorText", t),
        variant: "error",
      });
    } finally {
      setPreviewingContractId(null);
    }
  }

  async function handlePreviewJobApplication(candidateId: number) {
    if (previewingJobApplicationCandidateId !== null) return;

    try {
      setPreviewingJobApplicationCandidateId(candidateId);
      const { blob, filename } = await previewJobApplication(candidateId).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || t("downloads.jobApplicationFileName", { candidateId })));
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.previewErrorText", t),
        variant: "error",
      });
    } finally {
      setPreviewingJobApplicationCandidateId(null);
    }
  }

  async function handlePreviewOrder(orderId: number) {
    if (previewingOrderCandidateId !== null) return;

    try {
      setPreviewingOrderCandidateId(orderId);
      const { blob, filename } = await previewOrderOnHiring(orderId).unwrap();
      downloadBlob(blob, ensurePdfExtension(filename || t("downloads.orderFileName", { candidateId: orderId })));
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.previewErrorText", t),
        variant: "error",
      });
    } finally {
      setPreviewingOrderCandidateId(null);
    }
  }

  function handleDownloadOriginal(_id: number, contract?: CombinedContractListItem) {
    const url = contract?.signing_document?.original_file_url;
    if (url) {
      window.open(url, "_blank");
    }
  }

  return {
    fetchContractDetail,
    downloadingContractId,
    previewingContractId,
    previewingJobApplicationCandidateId,
    previewingOrderCandidateId,
    isDownloading,
    isPreviewing,
    handleDownload,
    handlePreview,
    handlePreviewJobApplication,
    handlePreviewOrder,
    handleDownloadOriginal,
    setPrompt,
  };
}
